import { useState, useEffect, useMemo, useCallback } from "react"
import API from "../../utils/API"
import Sweetalert from "../../utils/Sweetalert"
import useFeatureFlag, { FEATURE_CODES, doesTeamHaveAccessToFeature } from "../../hooks/useFeatureFlag"
import StatsFilters from "./StatsFilters"
import UnifiedKpiBar from "./UnifiedKpiBar"
import TrafficTrendChart from "./AudienceTraffic/TrafficTrendChart"
import DemographicsCharts from "./AudienceTraffic/DemographicsCharts"
import TargetedDefaultDoughnut from "./AdPerformance/TargetedDefaultDoughnut"
import "../Diffusion/AiTargeting/AiTargeting.css"
import "./AiTargetingStats.css"

function computeDateRange(period, customRange) {
  if (period === "custom" && customRange) {
    const start = new Date(customRange.startDate + "T00:00:00")
    const end = new Date(customRange.endDate + "T23:59:59.999")

    return { startDate: start.toISOString(), endDate: end.toISOString() }
  }

  const end = new Date()
  end.setHours(23, 59, 59, 999)
  const start = new Date()
  start.setHours(0, 0, 0, 0)

  switch (period) {
    case "today":
      break
    case "7d":
      start.setDate(start.getDate() - 6)
      break
    case "30d":
      start.setDate(start.getDate() - 29)
      break
    case "90d":
      start.setDate(start.getDate() - 89)
      break
    default:
      start.setDate(start.getDate() - 6)
  }

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  }
}

export default function AiTargetingStatsSection() {
  const { isActive: isAiTargetingFeatureActive, isLoading: featureLoading } = useFeatureFlag(FEATURE_CODES.AI_TARGETING)

  const [teamHasAiFeature, setTeamHasAiFeature] = useState(false)
  const [teamLoaded, setTeamLoaded] = useState(false)
  const [totems, setTotems] = useState([])
  const [totemsLoaded, setTotemsLoaded] = useState(false)
  const [selectedTotemIds, setSelectedTotemIds] = useState([])
  const [period, setPeriod] = useState("7d")
  const [customRange, setCustomRange] = useState(null)

  const { startDate, endDate } = useMemo(() => computeDateRange(period, customRange), [period, customRange])

  const queryParams = useMemo(() => {
    if (selectedTotemIds.length === 0) return null

    return `totemIds=${selectedTotemIds.join(",")}&startDate=${startDate}&endDate=${endDate}`
  }, [selectedTotemIds, startDate, endDate])

  const checkTeamAiFeature = useCallback(() => {
    API.GET("/user/team", (response) => {
      setTeamHasAiFeature(doesTeamHaveAccessToFeature(response.data, FEATURE_CODES.AI_TARGETING))
      setTeamLoaded(true)
    }, () => {
      setTeamHasAiFeature(false)
      setTeamLoaded(true)
    })
  }, [])

  useEffect(() => {
    checkTeamAiFeature()
  }, [checkTeamAiFeature])

  useEffect(() => {
    API.GET(
      "/totem",
      (response) => {
        const aiTotems = response.data.filter((t) => t.hasAiTargeting)
        setTotems(aiTotems)
        setSelectedTotemIds(aiTotems.map((t) => t.id))
        setTotemsLoaded(true)
      },
      () => {
        Sweetalert.fireErrorToast("Impossible de charger les totems")
        setTotemsLoaded(true)
      }
    )
  }, [])

  const accessLoading = featureLoading || !teamLoaded
  const featureNotAvailable = !accessLoading && (!isAiTargetingFeatureActive || !teamHasAiFeature)

  if (accessLoading) return null
  if (featureNotAvailable) return null

  return (
    <>
      {!totemsLoaded ? (
        <div className="overlay m-5">
          <i className="fas fa-2x fa-sync-alt fa-spin"></i>
        </div>
      ) : totems.length === 0 ? (
        <div className="row mt-4">
          <div className="col">
            <div className="card">
              <div className="card-body ai-stats-empty" style={{ minHeight: 200 }}>
                <div className="ai-stats-empty__inner">
                  <i className="fas fa-chart-line ai-stats-empty__icon"></i>
                  <p className="ai-stats-empty__text">
                    Aucun écran avec IA disponible.<br />
                    <small className="text-muted">Activez l'IA sur vos écrans depuis la page Diffusion.</small>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="ai-stats-container mt-4">
            <div className="ai-stats-hero">
              <div className="ai-stats-hero__badge">
                <i className="fas fa-crosshairs"></i>
              </div>
              <div className="ai-stats-hero__content">
                <h4 className="ai-stats-hero__title">Statistiques ciblage IA</h4>
                <p className="ai-stats-hero__subtitle">
                  Analysez les performances de vos publicités ciblées et comprenez votre audience
                </p>
              </div>
            </div>

            <StatsFilters
              totems={totems}
              selectedTotemIds={selectedTotemIds}
              onTotemChange={setSelectedTotemIds}
              period={period}
              onPeriodChange={setPeriod}
              customRange={customRange}
              onCustomRangeChange={setCustomRange}
            />

            {queryParams && (
              <>
                <div className="ai-stats-section">
                  <div className="ai-stats-section__step">1</div>
                  <div className="ai-stats-section__icon ai-stats-section__icon--overview">
                    <i className="fas fa-tachometer-alt"></i>
                  </div>
                  <div className="ai-stats-section__text">
                    <h5 className="ai-stats-section__title">Vue d'ensemble</h5>
                    <p className="ai-stats-section__subtitle">Les métriques clés de vos campagnes en un coup d'œil</p>
                  </div>
                  <span className="ai-stats-section__divider"></span>
                </div>
                <UnifiedKpiBar queryParams={queryParams} />

                <div className="ai-stats-scroll-hint">
                  <i className="fas fa-chevron-down ai-stats-scroll-hint__icon"></i>
                  <span>Explorez les détails ci-dessous</span>
                </div>

                <div className="ai-stats-section">
                  <div className="ai-stats-section__step">2</div>
                  <div className="ai-stats-section__icon ai-stats-section__icon--ads">
                    <i className="fas fa-bullseye"></i>
                  </div>
                  <div className="ai-stats-section__text">
                    <h5 className="ai-stats-section__title">Analysez vos performances</h5>
                    <p className="ai-stats-section__subtitle">Quelles publicités captent le mieux votre audience ?</p>
                  </div>
                  <span className="ai-stats-section__divider"></span>
                </div>
                <TargetedDefaultDoughnut queryParams={queryParams} />

                <div className="ai-stats-section">
                  <div className="ai-stats-section__step">3</div>
                  <div className="ai-stats-section__icon ai-stats-section__icon--audience">
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <div className="ai-stats-section__text">
                    <h5 className="ai-stats-section__title">Comprenez votre audience</h5>
                    <p className="ai-stats-section__subtitle">Découvrez qui regarde vos écrans pour affiner votre ciblage</p>
                  </div>
                  <span className="ai-stats-section__divider"></span>
                </div>
                <TrafficTrendChart queryParams={queryParams} />
                <DemographicsCharts queryParams={queryParams} />
              </>
            )}
          </div>
        </>
      )}
    </>
  )
}
